import React from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";

/**
 * Expects data as an array of objects:
 * [
 *   { month: '2025-01', count: 12 },
 *   ...
 * ]
 */
// Academic months order: Jul-Jun
// For X-axis: [7,8,9,10,11,12,1,2,3,4,5,6] (July to June)
const academicMonths = [7,8,9,10,11,12,1,2,3,4,5,6];
const academicMonthLabels = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];

// Group data by academic year and month
function groupByAcademicYear(data) {
  if (!Array.isArray(data)) return {};
  const grouped = {};
  data.forEach(d => {
    const dateStr = d.date || d.RegDate || d.month;
    if (!dateStr) return;
    const [y, m] = dateStr.split('-');
    const year = +y, month = +m;
    // Academic year starts in July
    let acadYear;
    if (month >= 7) acadYear = `${year}-${year+1}`;
    else acadYear = `${year-1}-${year}`;
    if (!grouped[acadYear]) grouped[acadYear] = {};
    grouped[acadYear][month] = (grouped[acadYear][month] || 0) + (d.count || 1);
  });
  return grouped;
}

// Build [{ month: 7, '2023-2024': 10, ... }, ...]
function buildLineChartData(grouped) {
  const acadYears = Object.keys(grouped).sort();
  // Output months in academic order (Jul-Jun)
  return academicMonths.map(m => {
    const obj = { month: m };
    acadYears.forEach(y => { obj[y] = grouped[y][m] || 0; });
    return obj;
  });
}



const lineColors = ['#e11d2e', '#ff4757', '#3fae6a', '#d99a3d', '#7a0d18'];

export default function MemberGrowthLineChart({ data }) {
  // Accepts [{RegDate: 'YYYY-MM-DD', ...}, ...] or [{date: ...}]
  const grouped = groupByAcademicYear(data);
  const acadYears = Object.keys(grouped).sort();
  const chartData = buildLineChartData(grouped);
  return (
    <div style={{    borderRadius: 18, boxShadow: '0 2px 16px #0006', padding: '24px 0', marginBottom: 24, width: '100%' }}>
      <h3 style={{ color: '#f1f4fb', fontWeight: 600, fontSize: 20, margin: '0 0 12px 32px' }}>Member Growth (Academic Year)</h3>
      <div
        role="img"
        aria-label={`Line chart of member growth by academic year across the months of July through June, for academic year(s): ${acadYears.join(', ')}.`}
      >
      <ResponsiveContainer width="100%" height={340}>
        <LineChart data={chartData} margin={{ top: 24, right: 36, left: 36, bottom: 12 }}>
          <CartesianGrid strokeDasharray="6 6" stroke="rgba(150,175,220,0.14)" />
          <XAxis
            dataKey="month"
            type="category"
            domain={academicMonths}
            ticks={academicMonths}
            tickFormatter={v => academicMonthLabels[academicMonths.indexOf(v)]}
            tick={{ fill: '#a7b6d6', fontSize: 14, fontWeight: 500 }}
            axisLine={{ stroke: 'rgba(150,175,220,0.3)' }}
            label={{ value: 'Month (Jul-Jun)', position: 'insideBottom', fill: '#a7b6d6', fontSize: 15, fontWeight: 600, offset: -5 }}
            interval={0}
            padding={{ left: 15, right: 15 }}
            allowDuplicatedCategory={false}
          />
          <YAxis
            tick={{ fill: '#a7b6d6', fontSize: 14, fontWeight: 500 }}
            allowDecimals={false}
            axisLine={{ stroke: 'rgba(150,175,220,0.3)' }}
            label={{ value: 'Members', angle: -90, position: 'insideLeft', fill: '#a7b6d6', fontSize: 15, fontWeight: 600, offset: 10 }}
            padding={{ top: 10, bottom: 10 }}
          />
          <Tooltip
            contentStyle={{ background: '#16274a', border: '1px solid rgba(150,175,220,0.14)', color: '#f1f4fb', borderRadius: 10, fontSize: 15 }}
            labelStyle={{ color: '#f1f4fb', fontWeight: 600 }}
            formatter={(value, name) => [`${value} members`, name]}
            labelFormatter={label => `Month: ${academicMonthLabels[academicMonths.indexOf(label)]}`}
          />
          <Legend wrapperStyle={{ color: '#f1f4fb', fontSize: 15, fontWeight: 600, paddingTop: 10 }} iconType="circle"/>
          {acadYears.map((year, idx) => (
            <Line
              key={year}
              type="monotone"
              dataKey={year}
              name={year}
              stroke={lineColors[idx % lineColors.length]}
              strokeWidth={3}
              dot={{ r: 4, fill: '#f1f4fb', stroke: lineColors[idx % lineColors.length], strokeWidth: 2 }}
              activeDot={{ r: 7, fill: lineColors[idx % lineColors.length], stroke: '#f1f4fb', strokeWidth: 2 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
}

